import { executeQuery, getFilePath, unreachableCase } from "../../../utils";
import { PageFooter } from "../Layouts/PageFooter";
import { ResponsiveImage } from "../ResponsiveImage";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { InviteEnterEmail } from "./InviteEnterEmail";
import { useEffect, useMemo, useState } from "react";
import { DmLoadingSpinner } from "../../../manager/utils/functions";
import { LearnerLicense } from "../../../manager/components/subscriptions/vouchers/types";
import { InviteError } from "./InviteError";
import { LicenseInviteState } from "./types";
import { InviteLogin } from "./InviteLogin";
import { InviteCreateAccount } from "./InviteCreateAccount";
import { InviteAccept } from "./InviteAccept";
import { InviteChoooseAccountType } from "./InviteChooseAccountType";
import { InviteSupportLink } from "./InviteSupportLink";

export const LicenseInvitePage: React.FC = () => {
  const { accessCode } = useParams<{ accessCode: string }>();
  const [inviteState, setInviteState] = useState<LicenseInviteState>({
    state: "loading",
  });
  const [accountType, setAccountType] = useState<"parent" | "learner">();
  const {
    data: licenseData,
    isLoading,
    error,
  } = useQuery<
    | { success: true; license: LearnerLicense }
    | { success: false; message: string }
  >({
    queryKey: ["getLicense", accessCode],
    queryFn: () =>
      executeQuery({ path: `/learner/default/license/${accessCode}` }),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const learner = user && user.entitlements ? user : null;

  const didInferAccountType = licenseData?.success
    ? licenseData.license.learnerQuantity > 1
    : false;

  const InviteContent = () => {
    switch (inviteState.state) {
      case "loading":
        return (
          <div className="mt-12 flex justify-center">
            <DmLoadingSpinner message="" />
          </div>
        );
      case "error":
        return <InviteError>{inviteState.message}</InviteError>;
      case "chooseAccountType":
        return (
          <InviteChoooseAccountType
            onSubmit={(a) => {
              setAccountType(a);
              setInviteState({ state: "enterEmail" });
            }}
          />
        );
      case "enterEmail":
        return licenseData?.success && accountType ? (
          <InviteEnterEmail
            isFirstSlide={didInferAccountType}
            accountType={accountType}
            setInviteState={setInviteState}
          />
        ) : null;
      case "login":
        return accountType ? (
          <InviteLogin
            email={inviteState.email}
            accountType={accountType}
            setInviteState={setInviteState}
          />
        ) : null;
      case "createAccount":
        return licenseData?.success && accountType ? (
          <InviteCreateAccount
            email={inviteState.email}
            accountType={accountType}
            setInviteState={setInviteState}
          />
        ) : null;
      case "acceptInvite":
        return licenseData?.success ? (
          <InviteAccept license={licenseData.license} />
        ) : null;
      default:
        return unreachableCase(inviteState);
    }
  };

  useEffect(() => {
    if (isLoading) {
      setInviteState({ state: "loading" });
    } else if (licenseData?.success) {
      if (licenseData.license.acceptedAt) {
        setInviteState({
          state: "error",
          message: (
            <p>
              This license has already been accepted. Please email{" "}
              <InviteSupportLink /> if you need further assistance.
            </p>
          ),
        });
      } else if (learner) {
        setInviteState({ state: "acceptInvite" });
      } else if (didInferAccountType) {
        setAccountType("parent");
        setInviteState({ state: "enterEmail" });
      } else {
        setInviteState({ state: "chooseAccountType" });
      }
    } else if (licenseData?.success === false) {
      setInviteState({
        state: "error",
        message: "There was an error loading the license.",
      });
    } else if (error) {
      setInviteState({
        state: "error",
        message: (error as any)?.error.includes("not found")
          ? "This license could not be found."
          : "Unable to load license data.",
      });
    }
  }, [isLoading, error, licenseData, learner, didInferAccountType]);

  return (
    <main className="flex min-h-dvh w-full flex-col items-center bg-dm-background-blue-100">
      <header className="mb-14 w-full px-6 py-6 sm:px-14">
        <img
          src={getFilePath("/images/DeltaMath-Logo_Home.svg")}
          alt="DeltaMath for Home"
        />
      </header>

      <div className="mb-36 flex w-full max-w-[332px] flex-grow flex-col justify-center gap-10 px-4">
        <InviteContent />
      </div>

      <div className="relative w-full overflow-hidden pb-6 sm:pb-[250px] lg:overflow-visible lg:pb-6">
        <PageFooter className="relative z-10" />
        <ResponsiveImage
          className="absolute -bottom-28 -left-10 z-0 hidden sm:block md:-bottom-20 lg:bottom-0 lg:left-0"
          srcs={[
            getFilePath("/images/learner/invite/deltie-thumbs.png"),
            getFilePath("/images/learner/invite/deltie-thumbs@2x.png"),
          ]}
          alt="Deltie waving hello"
          aria-hidden
        />
      </div>
    </main>
  );
};
